<template>
  <div class="page-view">

    <BasePageHeader :title="$t('profile.title')" @back="$router.back()" />

    <EmptyState v-if="loading" :message="$t('common.loading')" />
    <EmptyState v-else-if="loadError" :message="loadError" variant="error" />

    <template v-else>

      <!-- Profile section -->
      <div class="px-4 pt-5 pb-4 flex flex-col gap-3 bg-[var(--color-surface)] border-b border-[var(--color-border)]">
        <div class="section-label">{{ $t('profile.profileDetails') }}</div>

        <BaseFormField :label="$t('profile.firstName')">
          <BaseInput v-model="form.first_name" :placeholder="$t('profile.firstName')" maxlength="150" />
        </BaseFormField>
        <BaseFormField :label="$t('profile.lastName')">
          <BaseInput v-model="form.last_name" :placeholder="$t('profile.lastName')" maxlength="150" />
        </BaseFormField>
        <BaseFormField :label="$t('profile.email')">
          <BaseInput v-model="form.email" :placeholder="$t('profile.email')" type="email" maxlength="254" />
        </BaseFormField>
        <BaseFormField :label="$t('profile.phone')">
          <BaseInput v-model="form.phone" :placeholder="$t('profile.phone')" maxlength="20" />
        </BaseFormField>
        <BaseFormField :label="$t('profile.uniqueTag')">
          <BaseInput v-model="form.unique_tag" :placeholder="$t('profile.uniqueTagPlaceholder')" maxlength="120" />
        </BaseFormField>

        <div class="flex justify-end mt-1">
          <button class="btn primary sm" :disabled="savingProfile || !profileChanged" @click="saveProfile">
            {{ savingProfile ? $t('common.saving') : $t('profile.saveProfile') }}
          </button>
        </div>
      </div>

      <!-- Password section -->
      <div class="px-4 pt-5 pb-4 flex flex-col gap-3 bg-[var(--color-surface)] border-b border-[var(--color-border)]">
        <div class="section-label">{{ $t('profile.changePassword') }}</div>

        <div v-if="oidcBlocked" class="flex items-start gap-2 bg-[#1c1917] border border-[#44403c] rounded-lg p-3 text-sm text-[#a8a29e] leading-snug">
          <svg class="shrink-0 mt-0.5 text-[#78716c]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          {{ $t('profile.oidcPasswordInfo') }}
        </div>

        <template v-else>
          <BaseFormField :label="$t('profile.currentPassword')">
            <BaseInput v-model="pwForm.current_password" type="password" :placeholder="$t('profile.currentPassword')" autocomplete="current-password" />
          </BaseFormField>
          <BaseFormField :label="$t('profile.newPassword')">
            <BaseInput v-model="pwForm.new_password" type="password" :placeholder="$t('profile.newPasswordPlaceholder')" autocomplete="new-password" />
          </BaseFormField>
          <BaseFormField :label="$t('profile.confirmPassword')">
            <BaseInput v-model="pwForm.confirm_password" type="password" :placeholder="$t('profile.confirmPassword')" autocomplete="new-password" />
          </BaseFormField>
          <div v-if="pwMismatch" class="text-xs text-[#f87171]">{{ $t('profile.passwordMismatch') }}</div>
          <div class="flex justify-end mt-1">
            <button class="btn primary sm" :disabled="savingPw || !pwFormValid" @click="savePassword">
              {{ savingPw ? $t('common.saving') : $t('profile.changePassword') }}
            </button>
          </div>
        </template>
      </div>

      <!-- Role badge -->
      <div class="px-4 pt-5 pb-4 flex flex-col gap-2">
        <div class="section-label">{{ $t('profile.tenantRole') }}</div>
        <div
          class="badge"
          :class="
            ['tenant_admin', 'super_admin'].includes(tenantRole) ? 'badge-accent' :
            tenantRole === 'manager' ? 'badge-success' :
            tenantRole === 'warehouse_operator' ? 'badge-warning' :
            'badge-muted'
          "
        >{{ tenantRole ? tenantRole.replace(/_/g, ' ') : '…' }}</div>
      </div>

      <!-- Notification Preferences link -->
      <div class="px-4 pt-2 pb-4">
        <button
          class="data-card w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-[var(--color-surface-2)] transition-colors cursor-pointer"
          @click="router.push({ name: 'notification-preferences' })"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 text-[var(--color-muted)] shrink-0">
            <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/>
          </svg>
          <div class="flex flex-col min-w-0">
            <span class="text-sm font-medium text-[var(--color-text)]">{{ $t('profile.notificationPreferences') }}</span>
            <span class="text-xs text-[var(--color-muted)]">{{ $t('profile.notificationPrefsDesc') }}</span>
          </div>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 text-[var(--color-muted)] ml-auto shrink-0">
            <path d="M9 18l6-6-6-6"/>
          </svg>
        </button>
      </div>

    </template>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useToast } from '@/composables/useToast'
import { getMe, updateMe, changeMyPassword } from '@/api/api.js'
import { usePermissions } from '@/composables/usePermissions.js'
import BasePageHeader from '@/components/base/BasePageHeader.vue'
import BaseFormField from '@/components/base/BaseFormField.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import EmptyState from '@/components/base/EmptyState.vue'

const router = useRouter()
const { t } = useI18n()

const toast = useToast()
const { systemRoles } = usePermissions()

const loading   = ref(false)
const loadError = ref(null)

// profile form
const form         = ref({ first_name: '', last_name: '', email: '', phone: '', unique_tag: '' })
const savedForm    = ref({ first_name: '', last_name: '', email: '', phone: '', unique_tag: '' })
const savingProfile = ref(false)

const profileChanged = computed(() =>
  Object.keys(form.value).some(k => form.value[k] !== savedForm.value[k])
)

// password form
const pwForm       = ref({ current_password: '', new_password: '', confirm_password: '' })
const savingPw     = ref(false)
const oidcBlocked  = ref(false)

const tenantRole = computed(() => systemRoles.value[0] ?? '')

const pwMismatch = computed(() =>
  pwForm.value.confirm_password.length > 0 &&
  pwForm.value.new_password !== pwForm.value.confirm_password
)

const pwFormValid = computed(() =>
  pwForm.value.current_password.length > 0 &&
  pwForm.value.new_password.length >= 8 &&
  pwForm.value.new_password === pwForm.value.confirm_password
)

async function load() {
  loading.value = true
  loadError.value = null
  try {
    const me = await getMe()

    const profile = me.profile ?? {}
    form.value = {
      first_name: me.first_name ?? '',
      last_name:  me.last_name  ?? '',
      email:      me.email      ?? '',
      phone:      profile.phone      ?? '',
      unique_tag: profile.unique_tag ?? '',
    }
    savedForm.value = { ...form.value }
  } catch (e) {
    loadError.value = t('profile.loadFailed')
  } finally {
    loading.value = false
  }
}

async function saveProfile() {
  savingProfile.value = true
  try {
    const payload = {
      first_name: form.value.first_name,
      last_name:  form.value.last_name,
      email:      form.value.email,
      profile: {
        phone:      form.value.phone,
        unique_tag: form.value.unique_tag,
      },
    }
    const updated = await updateMe(payload)
    const profile = updated.profile ?? {}
    savedForm.value = {
      first_name: updated.first_name ?? '',
      last_name:  updated.last_name  ?? '',
      email:      updated.email      ?? '',
      phone:      profile.phone      ?? '',
      unique_tag: profile.unique_tag ?? '',
    }
    form.value = { ...savedForm.value }
    toast.success(t('profile.updated'))
  } catch {
    toast.error(t('profile.updateFailed'))
  } finally {
    savingProfile.value = false
  }
}

async function savePassword() {
  savingPw.value = true
  try {
    await changeMyPassword(pwForm.value.current_password, pwForm.value.new_password)
    pwForm.value = { current_password: '', new_password: '', confirm_password: '' }
    toast.success(t('profile.passwordChanged'))
  } catch (e) {
    if (e.status === 403) {
      oidcBlocked.value = true
      toast.error(t('profile.oidcPasswordDenied'))
    } else if (e.data?.detail || e.data?.current_password) {
      toast.error(e.data?.current_password?.[0] ?? e.data?.detail ?? t('profile.passwordChangeFailed'))
    } else {
      toast.error(t('profile.passwordChangeFailed'))
    }
  } finally {
    savingPw.value = false
  }
}

onMounted(load)
</script>


